import { Box, Button, Container, Paper, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import LoadingIndicator from "ec/components/LoadingIndicator";
import { Field, Form, Formik } from "formik";
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import AppPasswordField from "../forms/AppPasswordField";
import { useChangePassword } from "../userHooks";
import AppPage from ".";

const initialValues = {
  password: "",
};

const validate = (values) => {
  const errors = {};
  if (!values.password) {
    errors.password = "can't be blank";
  } else if (values.password.length < 6) {
    errors.password = "must be at least 6 characters long";
  }
  return errors;
};

const normalizeErrorMessage = (message) => (Array.isArray(message) ? message.join(", ") : message);

const splitErrors = (errors = {}) =>
  Object.entries(errors).reduce(
    (result, [field, message]) => {
      if (field === "base") {
        return {
          ...result,
          formError: normalizeErrorMessage(message),
        };
      }

      return {
        ...result,
        fieldErrors: {
          ...result.fieldErrors,
          [field]: normalizeErrorMessage(message),
        },
      };
    },
    { fieldErrors: {}, formError: undefined },
  );

const ChangePassword = () => {
  const [updatePassword, loading] = useChangePassword();
  const { email } = useSelector((state) => state.userProfile.data);
  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(
    (values, { setErrors, setStatus, setSubmitting }) => {
      setStatus(undefined);
      updatePassword(values)
        .then((data) => {
          if (data && data.errors) {
            const { fieldErrors, formError } = splitErrors(data.errors);
            setErrors(fieldErrors);
            setStatus(formError);
          } else {
            setSuccess(true);
          }
        })
        .finally(() => setSubmitting(false));
    },
    [updatePassword],
  );

  const navigate = useNavigate();
  const goHome = useCallback(() => navigate("/"), [navigate]);

  if (success) {
    return (
      <AppPage docTitle="Password Successfully Updated">
        <Container maxWidth="xs">
          <Box sx={{ my: 5 }}>
            <Alert
              variant="filled"
              severity="success"
              action={
                <Button variant="outlined" color="inherit" size="small" onClick={goHome}>
                  Continue
                </Button>
              }
            >
              Password Changed
            </Alert>
          </Box>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage docTitle="Change Password">
      <Container maxWidth="xs">
        <Box sx={{ my: 5 }}>
          {!email && <LoadingIndicator />}
          <Paper variant="outlined">
            <Box sx={{ p: 4 }}>
              <Formik
                validateOnBlur={false}
                validateOnChange={false}
                initialValues={initialValues}
                validate={validate}
                onSubmit={handleSubmit}
              >
                {({ status }) => (
                  <Form>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h5" component="h1">
                        Change Password
                      </Typography>
                    </Box>
                    {status && (
                      <Box sx={{ mb: 2 }}>
                        <Alert severity="error">{status}</Alert>
                      </Box>
                    )}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption">Email</Typography>
                      <Typography variant="body1">{email}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Field
                        name="password"
                        label="New Password"
                        as={AppPasswordField}
                        newPassword
                      />
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Button onClick={goHome}>Cancel</Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={loading}
                      >
                        Set Password
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Paper>
        </Box>
      </Container>
    </AppPage>
  );
};

export default ChangePassword;
